import { useEffect, useState } from "react";
import { X, Send, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ADDONS } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEPOSIT = 25;

export const BookingModal = ({ service, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", zip: "", date: "", notes: "" });
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [travel, setTravel] = useState({ fee: 0, label: "Enter a ZIP for travel fee", in_area: false, zip: "" });

  const toggleAddon = (id) => {
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  // Debounced travel-fee lookup whenever ZIP has 5 digits.
  useEffect(() => {
    const z = (form.zip || "").trim();
    if (z.length !== 5 || !/^\d{5}$/.test(z)) {
      setTravel({ fee: 0, label: "Enter a ZIP for travel fee", in_area: false, zip: "" });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/travel-fee`, { params: { zip: z } });
        setTravel(res.data || { fee: 0, label: "", in_area: false, zip: z });
      } catch {
        setTravel({ fee: 0, label: "Could not verify ZIP", in_area: false, zip: z });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [form.zip]);

  const addonTotal = selectedAddons.reduce(
    (sum, id) => sum + (ADDONS.find((a) => a.id === id)?.price || 0), 0
  );
  const travelFee = Number(travel.fee || 0);
  const basePrice = service.durations[0].price;
  const total = basePrice + addonTotal + travelFee;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/booking-checkout`, {
        service_id: service.id,
        ...form,
        addon_ids: selectedAddons,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please call us at (865) 221-7543.";
      toast.error(typeof msg === "string" ? msg : "Payment could not be initialized.");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="booking-modal"
      className="fixed inset-0 z-50 bg-spa-dark/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-spa-cream w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl p-6 sm:p-10 my-0 sm:my-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="overline text-spa-gold mb-2">Booking Request</p>
            <h3 className="font-serif text-3xl text-spa-dark">{service.name}</h3>
            <p className="text-spa-gray mt-1 text-sm">
              {service.durations[0].label} · ${service.durations[0].price} (default)
            </p>
          </div>
          <button onClick={onClose} data-testid="booking-modal-close" aria-label="Close" className="p-1 text-spa-gray hover:text-spa-dark">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="booking-input-name" className="input-spa" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="booking-input-phone" className="input-spa" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <input data-testid="booking-input-email" type="email" className="input-spa" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="booking-input-address" className="input-spa" placeholder="Service address (street, city)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-start">
            <input
              data-testid="booking-input-zip"
              className="input-spa sm:col-span-2"
              placeholder="ZIP code"
              inputMode="numeric"
              maxLength={5}
              value={form.zip}
              onChange={(e) => setForm({ ...form, zip: e.target.value.replace(/\D/g, "") })}
            />
            <div
              data-testid="booking-travel-fee"
              className={`sm:col-span-3 rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
                travel.in_area
                  ? "bg-white border-spa-border text-spa-dark"
                  : "bg-white border-spa-border text-spa-gray"
              }`}
            >
              <MapPin className="w-4 h-4 text-spa-gold shrink-0" strokeWidth={1.5} />
              <span className="truncate">{travel.label}</span>
              {travelFee > 0 && (
                <span className="ml-auto font-serif text-base text-spa-dark whitespace-nowrap">+${travelFee}</span>
              )}
            </div>
          </div>

          <input data-testid="booking-input-date" className="input-spa" placeholder="Preferred date & time (e.g., Sat Mar 15, afternoon)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />

          <div>
            <p className="overline mb-3">Add-ons (optional)</p>
            <div className="space-y-2">
              {ADDONS.map((a) => {
                const checked = selectedAddons.includes(a.id);
                return (
                  <button
                    type="button"
                    key={a.id}
                    data-testid={`booking-addon-${a.id}`}
                    onClick={() => toggleAddon(a.id)}
                    aria-pressed={checked}
                    className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-all ${
                      checked
                        ? "bg-spa-green text-spa-cream border-spa-green"
                        : "bg-white border-spa-border text-spa-dark hover:border-spa-green"
                    }`}
                  >
                    <span className="flex items-center gap-2.5 text-sm">
                      <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${checked ? "border-spa-gold bg-spa-gold" : "border-spa-border"}`}>
                        {checked && <CheckCircle2 className="w-3 h-3 text-spa-green" strokeWidth={2} />}
                      </span>
                      {a.name}
                    </span>
                    <span className="font-serif text-lg whitespace-nowrap">+${a.price}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <textarea data-testid="booking-input-notes" className="input-spa min-h-[100px] resize-none" placeholder="Any focus areas, sensitivities, or notes?" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

          <div className="pt-3 border-t border-spa-border space-y-1 text-sm text-spa-gray">
            <div className="flex items-center justify-between">
              <span>Session</span>
              <span>${basePrice}</span>
            </div>
            {addonTotal > 0 && (
              <div className="flex items-center justify-between">
                <span>Add-ons</span>
                <span>+${addonTotal}</span>
              </div>
            )}
            {travelFee > 0 && (
              <div className="flex items-center justify-between">
                <span>Travel ({travel.zip || "ZIP"})</span>
                <span>+${travelFee}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-spa-border/60">
              <span className="text-spa-dark">Session total</span>
              <span data-testid="booking-total" className="font-serif text-2xl text-spa-dark">${total}</span>
            </div>
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-spa-border/60">
              <span className="text-spa-dark font-medium">Due today (deposit)</span>
              <span data-testid="booking-deposit" className="font-serif text-3xl text-spa-dark">${DEPOSIT}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Balance at your session</span>
              <span data-testid="booking-balance" className="text-spa-dark">${Math.max(0, total - DEPOSIT)}</span>
            </div>
          </div>

          <button data-testid="booking-submit" type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? "Redirecting to Stripe..." : <>Pay ${DEPOSIT} Deposit & Reserve <Send className="w-4 h-4" strokeWidth={1.5} /></>}
          </button>
          <p className="text-xs text-spa-gray text-center">
            A ${DEPOSIT} deposit reserves your session. The remaining ${Math.max(0, total - DEPOSIT)} is paid directly to your therapist at the appointment. Secure checkout via Stripe.
          </p>
        </form>
      </div>
    </div>
  );
};
